import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppProvider, useApp } from './AppContext';
import { MockModeProvider, useMockMode } from './MockModeContext';
import { AppShell } from '../design/shell';
import { getCrowdC } from '../design/crowdPalette';
import { LoginScreen } from './login';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AdminScreen } from '../app/screens/admin';
import { AlertsScreen } from '../app/screens/alerts';
import { CommandScreen } from '../app/screens/command';
import { FlaggingScreen } from '../app/screens/flagging';
import { MapDashboard } from '../app/screens/map-dashboard';
import { MovementScreen } from '../app/screens/movement';
import { OperationsScreen } from '../app/screens/operations';
import { ReportingScreen } from '../app/screens/reports';
import { VenuesScreen } from '../app/screens/venues';
import { ZonesScreen } from '../app/screens/zones';
import { CROWD_ROLES, loginPath } from '@vigilo/security';
import { TOKEN_KEY } from '@vigilo/api-client';
import { canViewCommandDashboard } from '../app/utils/operation-entry-routing';
import { CROWD_CLIENT_ACTIVITY_ACTION, useCrowdActivityAudit } from '../app/hooks/useCrowdActivityAudit';

const BorderSecurityApp = lazy(() => import('@vigilo/border-security-web').then(module => ({ default: module.BorderSecurityApp })));
const CriticalInfraApp = lazy(() => import('@vigilo/critical-infrastructure-web').then(module => ({ default: module.CriticalInfraApp })));
const ForensicsApp = lazy(() => import('@vigilo/forensics-web').then(module => ({ default: module.ForensicsApp })));
const IntelApp = lazy(() => import('@vigilo/intel-web').then(module => ({ default: module.IntelApp })));
// ─── Layout route ─────────────────────────────────────────────────────────────

function AppLayout() {
  const { theme, currentUser, selectedOperation } = useApp();
  const { isMock } = useMockMode();
  const recordActivity = useCrowdActivityAudit();
  const location = useLocation();
  const navigate = useNavigate();
  const C = getCrowdC(theme);

  useEffect(() => {
    if (!location.pathname.startsWith('/border')) {
      document.title = 'Vigilo - Crowd Monitoring';
    }
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY());
    if (!token) return;
    if (!CROWD_ROLES.has(String(currentUser?.role ?? ''))) {
      navigate(loginPath('crowd', 'unauthorized'), { replace: true });
    }
  }, [currentUser?.role, navigate]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY());
    if (isMock || !token || !CROWD_ROLES.has(String(currentUser?.role ?? ''))) return;
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.pageViewed,
      page: location.pathname,
      operationId: selectedOperation?.id,
    });
  }, [currentUser?.role, isMock, location.pathname, recordActivity, selectedOperation?.id]);

  return (
    <AppShell C={C}>
      <Outlet />
    </AppShell>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

function OperationRequiredRoute() {
  const { selectedOperation, operationsResolved } = useApp();
  const location = useLocation();

  // A hard reload lands here before the persisted operation has been restored; wait rather than bounce to Operations.
  if (!selectedOperation && !operationsResolved) return null;

  if (!selectedOperation) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/operations?next=${encodeURIComponent(next)}`} replace />;
  }

  return <Outlet />;
}

function CommandDashboardRequiredRoute({ children }: { children: JSX.Element }) {
  const { currentUser } = useApp();

  if (!canViewCommandDashboard(currentUser.role)) {
    return <Navigate to="/operations" replace />;
  }

  return children;
}

function canViewAdministration(role?: string | null): boolean {
  return role === 'system_admin';
}

function AdminRequiredRoute({ children }: { children: JSX.Element }) {
  const { currentUser } = useApp();

  if (!canViewAdministration(currentUser.role)) {
    return <Navigate to="/operations" replace />;
  }

  return children;
}

export default function App() {
  return (
    <MockModeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppProvider>
          <Routes>
            <Route path="/login" element={<LoginScreen />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/border/*"    element={<Suspense fallback={null}><BorderSecurityApp /></Suspense>} />
              <Route path="/ci/*"        element={<Suspense fallback={null}><CriticalInfraApp /></Suspense>} />
              <Route path="/forensics/*" element={<Suspense fallback={null}><ForensicsApp /></Suspense>} />
              <Route path="/intel/*"     element={<Suspense fallback={null}><IntelApp /></Suspense>} />
              <Route element={<AppLayout />}>
                <Route index element={<Navigate to="/operations" replace />} />
                <Route path="/operations" element={<OperationsScreen />} />
                <Route element={<OperationRequiredRoute />}>
                  <Route path="/map"      element={<MapDashboard />} />
                  <Route path="/zones"    element={<ZonesScreen />} />
                  <Route path="/alerts"   element={<AlertsScreen />} />
                  <Route path="/command"  element={<CommandDashboardRequiredRoute><CommandScreen /></CommandDashboardRequiredRoute>} />
                  <Route path="/movement" element={<MovementScreen />} />
                  <Route path="/flags"    element={<FlaggingScreen />} />
                  <Route path="/reports"  element={<ReportingScreen />} />
                </Route>
                <Route path="/admin/zones" element={<ZonesScreen mode="catalog" />} />
                <Route path="/venues"   element={<VenuesScreen />} />
                <Route path="/admin"    element={<AdminRequiredRoute><AdminScreen /></AdminRequiredRoute>} />
                <Route path="*"         element={<Navigate to="/operations" replace />} />
              </Route>
            </Route>
          </Routes>
        </AppProvider>
      </BrowserRouter>
    </MockModeProvider>
  );
}
